/**
 * Attentive behavioural tracking consent.
 *
 * Attentive loads via its Shopify app embed, so this only toggles tracking on the
 * already-loaded tag: attentive.analytics.enable() / disable().
 *
 * US is an opt-out regime (tracking on until the shopper opts out of the OneTrust
 * analytics group); every other market is opt-in, with Attentive holding tracking
 * off server-side until we call enable().
 */
(function () {
  var w =
    /** @type {Window & { __consentConfig?: { analyticsCategoryId?: string; countryIsoCode?: string }; OptanonActiveGroups?: string; attentive?: { analytics?: { enable?: () => void; disable?: () => void } } }} */ (
      window
    );

  var config = w.__consentConfig || {};
  var group = config.analyticsCategoryId || 'C0002';
  var isUS = String(config.countryIsoCode || '').toUpperCase() === 'US';

  // OneTrust can write OptanonActiveGroups slightly after its events fire.
  var RECHECK_DELAYS = [150, 400, 1000];
  // The app embed loads async, so the API may not exist when consent resolves.
  var POLL_INTERVAL = 200;
  var POLL_LIMIT = 15000;

  /** @type {boolean | null} */
  var lastApplied = null;
  /** @type {boolean | null} */
  var pending = null;
  /** @type {ReturnType<typeof setInterval> | null} */
  var pollTimer = null;
  var errorLogged = false;

  /**
   * Strict read of the OneTrust group: null when consent is not yet determined.
   * Deliberately not window.hasConsent, which fails open (returns true) before
   * OneTrust has loaded — that would enable tracking for opt-in markets.
   * @returns {boolean | null}
   */
  function readConsent() {
    var groups = typeof w.OptanonActiveGroups === 'string' ? w.OptanonActiveGroups : '';
    if (!groups) return null;
    return (',' + groups + ',').indexOf(',' + group + ',') !== -1;
  }

  function hasApi() {
    var api = w.attentive && w.attentive.analytics;
    return !!api && typeof api.enable === 'function' && typeof api.disable === 'function';
  }

  /**
   * enable() and disable() are not in Attentive's public docs, so log a failure once
   * rather than silently dropping it.
   * @param {unknown} error
   */
  function logOnce(error) {
    if (errorLogged) return;
    errorLogged = true;
    if (typeof console !== 'undefined' && console.error) console.error('Attentive consent:', error);
  }

  /** @param {boolean} enabled */
  function applyState(enabled) {
    if (lastApplied === enabled) return;

    var api = w.attentive && w.attentive.analytics;
    var toggle = api && (enabled ? api.enable : api.disable);
    if (typeof toggle === 'function') {
      try {
        toggle.call(api);
        lastApplied = enabled;
        pending = null;
        stopPoll();
        return;
      } catch (error) {
        logOnce(error);
      }
    }

    // API missing or threw: queue the state so the bounded poll retries it.
    pending = enabled;
    startPoll();
  }

  function startPoll() {
    if (pollTimer !== null) return;
    var elapsed = 0;
    pollTimer = setInterval(function () {
      elapsed += POLL_INTERVAL;
      if (pending === null) {
        stopPoll();
        return;
      }
      if (hasApi()) applyState(pending);
      if (elapsed >= POLL_LIMIT) stopPoll();
    }, POLL_INTERVAL);
  }

  function stopPoll() {
    if (pollTimer === null) return;
    clearInterval(pollTimer);
    pollTimer = null;
  }

  function evaluate() {
    var consented = readConsent();
    if (consented === null) {
      // Consent unknown: leave the US default (tracking on) untouched, and keep
      // opt-in markets off in case the Attentive-side default is not in effect.
      if (!isUS) applyState(false);
      return;
    }
    applyState(consented);
  }

  function evaluateWithRechecks() {
    evaluate();
    for (var i = 0; i < RECHECK_DELAYS.length; i++) {
      setTimeout(evaluate, RECHECK_DELAYS[i]);
    }
  }

  // consent-helper.js re-fires consent-ready on OneTrustGroupsUpdated, so banner and
  // preference-centre changes both land here. Never define OptanonWrapper — dynamic-yield
  // owns the global one and a second declaration would clobber its consent sync.
  w.addEventListener('consent-ready', evaluateWithRechecks);
  w.addEventListener('OTConsentApplied', evaluateWithRechecks);

  // This script is deferred and runs after consent-helper.js, which may already have
  // dispatched consent-ready synchronously, so evaluate the current state immediately.
  evaluateWithRechecks();
})();
